import React, { useEffect, useMemo, useState } from 'react';
import { Table, Typography, Button } from 'antd';
import { DownOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getProjectTimeline } from '../../../../utils/getProjectTimeline';
import CheckboxModel from '../../CustomModel/CheckboxModel/CheckboxModel';
import './ProjectTable.scss';
import VendorModel from '../../CustomModel/VendorModel/VendorModel';
import {
        addPackage,
        deletePackages,
        fetchRegionProjectDates,
        fetchUnitCommercialOptions,
        getProjectCost,
} from '../../../../redux/budgetSlice/budgetSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
        generateMonthExecution,
        transformData,
} from '../../../../pages/BudgetSubmission/constants';
import { addProduct, fetchProductType } from '../../../../redux/unitSlice/unitSlice';
import Loader from '../../LoadingSpinner/LoadingSpinner';
import { computeTotalRow } from '../../../../utils/VerticalTotalOfTable';

import { generateColumns, parseDMY } from '../../../../utils/utilFunctions';
import { addMonths, differenceInMonths, format, parse } from 'date-fns';
import { fetchPackages } from '../../../../redux/packageSlice/packageSlice';

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: CCI_UPTO was rendered straight off the API's static field, and the
// "Upto" title was anchored to this project's own UPTO_DATE.
// NEW: CCI Upto = the API's baseline CCI_UPTO plus every FY's "_formatted"
// value ("YYYY-YYYY_formatted") that falls strictly before the current
// (system-date-derived) FY - same "dynamic upto" rule used by
// ProjectWiseReport/UnitWiseReport/IUTReport etc.
const getDynamicCCIUpto = (row = {}, fyStart) => {
        let sum = parseNum(row.CCI_UPTO);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const ProjectCostTable = ({ addPackageButton = true, version = "C" }) => {
        const { Text } = Typography;
        const { user } = useSelector((state) => state.auth);
        const [searchParams] = useSearchParams();
        const unitType = searchParams?.get('unitType')?.toUpperCase(); // automatically decoded
        const loginType = unitType ?? user?.role; // if unitType is available, use it; otherwise, use user.role
        const { projectDates, projectCostData, projectCostVersion, loading } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);

        // OLD: no dynamic FY anchor was used for CCI Upto - it just read the API's static field.
        // NEW: derive it from the admin-configured System Start Date, falling back
        // to today's date until systemDate has loaded.
        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        const { productType, productListingData } = useSelector((state) => state.unit);

        const dispatch = useDispatch();
        const { unitCommercialOption } = useSelector((state) => state.budget);
        const location = useLocation();
        const ioNo = location.state?.ioNo || '--';
        const revisedNo = location.state?.rvNo || '--';
        const formattedData = unitCommercialOption?.map((item) => ({
                label: item.COMMERCIAL_NAME,
                value: item.COMMERCIAL_CODE,
        }));
        const productTypeOptions = productType.map((item) => ({
                value: item.PRODUCT_TYPE_CODE,
                label: item.PRODUCT_TYPE,
        }));
        const productOptions = productListingData.map((item) => ({
                code: item.UNIT_CODE,
                description: item.PRODUCT_NAME,
                typeCode: item.PRODUCT_CODE,
                type: item.UNIT_NAME,
        }));
        const [selectedPackages, setSelectedPackages] = useState([]);
        const [unitCommercial, setUnitCommercial] = useState();
        const [isModalOpen, setIsModalOpen] = useState(false);
        // OLD: CCI Upto title used to be anchored to this project's own UPTO_DATE.
        // Now driven by the system-date-derived CURRENT_FY_START instead (see columns below).
        // const formattedUptoDate = projectDates?.UPTO_DATE
        //         ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy")
        //         : '';
        const zeroDateParsed = parseDMY(projectDates?.ZERO_DATE);
        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanDateParsed = parseDMY(projectDates?.UNIT_PLAN_DATE);
        // const endYear = expectedDateParsed ? expectedDateParsed.getFullYear() : 2026;
        // let maxDataEndYear = 0;

        const sourceData =
                version === "C" ? projectCostData : projectCostVersion;

        if (sourceData?.length > 0) {
                const firstRow = sourceData[0];

                const fyKeys = Object.keys(firstRow).filter(
                        (key) => /^\d{4}-\d{4}$/.test(key) // Match "YYYY-YYYY"
                );

                // if (fyKeys.length > 0) {
                //         maxDataEndYear = Math.max(
                //                 ...fyKeys.map((fy) => Number(fy.split("-")[1]))
                //         );
                // }
        }

        // const expectedEndYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 0;

        // const endYear =
        //         expectedEndYear > maxDataEndYear
        //                 ? expectedEndYear
        //                 : maxDataEndYear;

        // const endMonth = expectedDateParsed ? expectedDateParsed.getMonth() + 1 : 12;

        let startMonth = 4;
        let startYear = 2025;

        // const expectedEndYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 0;

        // const unitPlanEndYear = unitPlanDateParsed
        //         ? unitPlanDateParsed.getFullYear()
        //         : 0;

        // // Get the higher year between EXPECTED_DATE and UNIT_PLAN_DATE
        // const higherDateYear = Math.max(expectedEndYear, unitPlanEndYear);

        // // combine both cases
        // const finalEndYear = higherDateYear
        // // convert FY end → FY start 
        // const endYear = finalEndYear ? finalEndYear : startYear ;

        // const unitPlanEndYear = unitPlanDateParsed
        //         ? unitPlanDateParsed.getFullYear()
        //         : 0;

        // Only use UNIT_PLAN_DATE
        // const finalEndYear = unitPlanEndYear;
        // // convert FY end → FY start 
        // const endYear = finalEndYear ? finalEndYear : startYear;


        // Dynamic end month based on higher date

        const role = sessionStorage.getItem('role');


        // OLD: rendered columns started the month after this project's own
        // UPTO_DATE.
        // NEW: anchored to the system-date-derived current FY (April of
        // CURRENT_FY_START) instead - consistent with CCI Upto, which now
        // buckets everything strictly before this FY, so columns render from
        // this FY onward.
        // if (uptoDateParsed) {
        //         const startMonthDate = addMonths(uptoDateParsed, 1); //  Move to next month
        //         startMonth = startMonthDate.getMonth() + 1; // 1-based month
        //         startYear = startMonthDate.getFullYear();
        // }
        startMonth = 4;
        startYear = CURRENT_FY_START;

        const startMonthNumber =
                zeroDateParsed && uptoDateParsed
                        ? differenceInMonths(uptoDateParsed, zeroDateParsed) + 3
                        : 0;

        // Use the higher of EXPECTED_DATE and UNIT_PLAN_DATE for total month calculation
        // const higherEndDate = expectedDateParsed && unitPlanDateParsed
        //         ? (expectedDateParsed > unitPlanDateParsed ? expectedDateParsed : unitPlanDateParsed)
        //         : (expectedDateParsed || unitPlanDateParsed);
        // const endMonth = higherEndDate ? higherEndDate.getMonth() + 1 : 12;

        // const totalMonth =
        //         zeroDateParsed && higherEndDate
        //                 ? differenceInMonths(higherEndDate, zeroDateParsed) + 3
        //                 : 0;

        // Use ONLY UNIT_PLAN_DATE for total month calculation
        // const endMonth = unitPlanDateParsed ? unitPlanDateParsed.getMonth() + 1 : 12;

        // const totalMonth =
        //         zeroDateParsed && unitPlanDateParsed
        //                 ? differenceInMonths(unitPlanDateParsed, zeroDateParsed) + 3
        //                 : 0;
        const { endYear, endMonth, totalMonth, higherEndDate } = getProjectTimeline({
                zeroDate: zeroDateParsed,
                expectedDate: expectedDateParsed,
                unitPlanDate: unitPlanDateParsed,
        });
        const [data, setData] = useState([]);
        const [dropdownState, setDropdownState] = useState({}); // Store dropdown states dynamically;
        const [selectedCategory, setSelectedCategory] = useState(null); // Track clicked section
        const [headtType, setHeadtType] = useState(null);

        const toggleDropdown = (category) => {
                setDropdownState((prev) => ({
                        ...prev,
                        [category]: !prev[category],
                }));
        };

        const handleAddPackage = (category) => {
                setSelectedCategory(category); // Set category (Installation or Civil Works)
                if (category) {
                        setHeadtType(category);
                }
                setIsModalOpen(true);
        };

        const handleAddProduct = (category) => {
                setSelectedCategory(category);
                if (category) {
                        setHeadtType(category);
                }
                setIsModalOpen(true);
        };

        const handleDeletePackages = (record) => {
                const commonParams = {
                        IO_NO: record?.IO_NO,
                        REV_NO: record?.REV_NO,
                        HEAD_TYPE: record?.HEAD_TYPE,
                        type: 'PC',
                        COMMERCIAL_CODE: record?.COMMERCIAL_CODE,
                };

                const deleteParams =
                        loginType === 'UNIT'
                                ? {
                                        ...commonParams,
                                        PRODUCT_CODE: record?.PRODUCT_CODE,
                                        PRODUCT_TYPE_CODE: record?.PRODUCT_TYPE_CODE,
                                }
                                : {
                                        ...commonParams,
                                        PACKAGE_ID: record?.PACKAGE_ID,
                                };

                dispatch(deletePackages(deleteParams)).then(() => {
                        dispatch(getProjectCost({ IO_NO: ioNo, REV_NO: revisedNo }));
                });
        };

        let columns = [
                {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        align: 'center',

                        children: [
                                {
                                        title: 'Head',
                                        dataIndex: 'DESC',
                                        key: 'DESC',
                                        fixed: 'left',
                                        width: 100,
                                        align: 'center',

                                        render: (text, record, index) => {
                                                if (record.ADD_PACKAGE_STATUS === 1) {
                                                        return {
                                                                children: (
                                                                        <div className="w-full">
                                                                                <Button
                                                                                        type="icon"
                                                                                        size="small"
                                                                                        icon={
                                                                                                <DownOutlined />
                                                                                        }
                                                                                        onClick={() =>
                                                                                                toggleDropdown(
                                                                                                        record.DESC,
                                                                                                )
                                                                                        }
                                                                                        className={
                                                                                                'custom_button'
                                                                                        }
                                                                                />
                                                                                <Text>{text}</Text>{' '}
                                                                                &nbsp;
                                                                                {addPackageButton &&
                                                                                        loginType ===
                                                                                        'REGION' && (
                                                                                                <Button
                                                                                                        type="link"
                                                                                                        icon={
                                                                                                                <PlusOutlined />
                                                                                                        }
                                                                                                        onClick={() => {
                                                                                                                const headType =
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record?.CODE;
                                                                                                                dispatch(
                                                                                                                        fetchPackages(
                                                                                                                                {
                                                                                                                                        headType,
                                                                                                                                },
                                                                                                                        ),
                                                                                                                );
                                                                                                                handleAddPackage(
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record?.CODE,
                                                                                                                );
                                                                                                        }}
                                                                                                >
                                                                                                        Add
                                                                                                        Package
                                                                                                </Button>
                                                                                        )}
                                                                                {addPackageButton &&
                                                                                        loginType ===
                                                                                        'UNIT' && (
                                                                                                <Button
                                                                                                        type="link"
                                                                                                        icon={
                                                                                                                <PlusOutlined />
                                                                                                        }
                                                                                                        onClick={() => {
                                                                                                                const headType =
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record?.CODE;
                                                                                                                dispatch(
                                                                                                                        fetchProductType(
                                                                                                                                {
                                                                                                                                        headType:
                                                                                                                                                record?.HEAD_TYPE ||
                                                                                                                                                record?.CODE,
                                                                                                                                        projectType:
                                                                                                                                                'PC',
                                                                                                                                },
                                                                                                                        ),
                                                                                                                );
                                                                                                                handleAddProduct(
                                                                                                                        record?.HEAD_TYPE ||
                                                                                                                        record.CODE,
                                                                                                                );
                                                                                                        }}
                                                                                                >
                                                                                                        Add
                                                                                                        Product
                                                                                                </Button>
                                                                                        )}
                                                                        </div>
                                                                ),
                                                                // props: { colSpan: 4 },
                                                        };
                                                }

                                                if (record.boldHeading) {
                                                        return (
                                                                <>
                                                                        <Text strong>{text}</Text>{' '}
                                                                        &nbsp;
                                                                </>
                                                        );
                                                }

                                                return <div className="w-[200px]">{text}</div>;
                                        },
                                },
                                {
                                        title: 'Unit Commercial',
                                        dataIndex: 'COMMERCIAL_NAME',
                                        key: 'COMMERCIAL_NAME',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Type'
                                                        : 'Package Type',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_TYPE'
                                                        : 'PACKAGE_TYPE',
                                        key: loginType === 'UNIT' ? 'PRODUCT_TYPE' : 'PACKAGE_TYPE',
                                        width: 150,
                                        fixed: 'left',
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Name'
                                                        : 'Package Name',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_NAME'
                                                        : 'PACKAGE_NAME',
                                        key: loginType === 'UNIT' ? 'PRODUCT_NAME' : 'PACKAGE_NAME',
                                        width: 150,
                                        fixed: 'left',
                                        align: 'center',
                                        render: (text, record) => {
                                                return (
                                                        <div className="flex justify-between w-full">
                                                                <div className="flex items-center justify-center w-[90%]">
                                                                        {text}
                                                                </div>
                                                                {(record.PACKAGE_NAME ||
                                                                        record.PRODUCT_NAME) &&
                                                                        addPackageButton && (
                                                                                <Button
                                                                                        type="text"
                                                                                        icon={
                                                                                                <DeleteOutlined
                                                                                                        style={{
                                                                                                                color: '#8c8c8c',
                                                                                                        }}
                                                                                                />
                                                                                        }
                                                                                        onClick={() =>
                                                                                                handleDeletePackages(
                                                                                                        record,
                                                                                                )
                                                                                        }
                                                                                        className="w-[10%]"
                                                                                />
                                                                        )}
                                                        </div>
                                                );
                                        },
                                },
                        ],
                },
                {
                        // OLD: title: `CCI Upto ${formattedUptoDate}`, (anchored to this project's own UPTO_DATE)
                        // NEW: anchored to the system-date-derived current FY, same as the other reports' "Upto Mar" labels.
                        title: `CCI Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'CCI_UPTO',
                        key: 'CCI_UPTO',
                        width: 150,
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        useEffect(() => {
                if (role !== 'ADMIN' && role !== 'SUPER_ADMIN') {
                        dispatch(fetchUnitCommercialOptions());
                        dispatch(getProjectCost({ IO_NO: ioNo, REV_NO: revisedNo }));
                        dispatch(fetchRegionProjectDates({ ioNo, revNo: revisedNo }));
                }
                dispatch(fetchProductType());
        }, [dispatch]);

        useEffect(() => {
                if (selectedPackages.length === 0 || !selectedCategory) return;
                setSelectedCategory(null); // Reset category
        }, [selectedPackages]);

        useEffect(() => {
                const sourceData =
                        version === "C" ? projectCostData : projectCostVersion;

                if (sourceData && sourceData.length > 0) {
                        // OLD: transformData/computeTotalRow read CCI_UPTO straight off the
                        // API's static field.
                        // NEW: replace it with the dynamic value on every row first, so both
                        // the per-row column and the generic Total-row sum stay consistent.
                        const sourceDataWithDynamicUpto = sourceData.map((row) => ({
                                ...row,
                                CCI_UPTO: getDynamicCCIUpto(row, CURRENT_FY_START),
                        }));

                        const transformedData = transformData({
                                data: sourceDataWithDynamicUpto,
                                dispatch,
                        });

                        const totalRow = computeTotalRow(sourceDataWithDynamicUpto);

                        if (
                                "PACKAGE_NAME" in totalRow ||
                                "PRODUCT_NAME" in totalRow
                        ) {
                                delete totalRow.PACKAGE_NAME;
                                delete totalRow.PRODUCT_NAME;
                                delete totalRow.PRODUCT_TYPE;
                                delete totalRow.COMMERCIAL_NAME;
                                delete totalRow.PACKAGE_TYPE;
                        }

                        setData([
                                {
                                        key: "1",
                                        DESC: "Month of Execution",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                                totalRow,
                                ...transformedData.map((item, index) => ({
                                        key: `projectCost-${index + 3}`,
                                        ...item,
                                })),
                        ]);
                } else {
                        // Show empty data with 0 values when no data from API
                        setData([
                                {
                                        key: "1",
                                        DESC: "Month of Execution",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                                {
                                        key: "2",
                                        DESC: "Total",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                        ]);
                }
        }, [projectCostData, projectCostVersion, version, CURRENT_FY_START]);


        return (
                <>
                        {loading ? (
                                <Loader />
                        ) : (
                                <Table
                                        columns={generateColumns(
                                                startYear,
                                                endYear,
                                                startMonth,
                                                endMonth,
                                                columns,
                                                higherEndDate || projectDates?.EXPECTED_DATE || projectDates?.UNIT_PLAN_DATE,
                                        )}
                                        dataSource={data.filter((item) => {
                                                if (!item.isPackage) return true; // Keep normal rows
                                                return dropdownState[item.package_row] || false; // Show/hide package rows dynamically
                                        })}
                                        pagination={false}
                                        bordered
                                        scroll={{ x: 'max-content', y: 400 }} // Ensures full-width scrolling
                                        className="custom-ant-table"
                                        rowClassName={(record, index) => {
                                                if (index === 0) {
                                                        return 'table-row-dark';
                                                } else if (index === 1) {
                                                        return 'table-row-light-blue';
                                                } else {
                                                        return 'table-row-light';
                                                }
                                        }}
                                />
                        )}
                        <div className="p-4">
                                {isModalOpen && (
                                        <>
                                                {loginType === 'UNIT' && (
                                                        <VendorModel
                                                                isOpen={loginType === 'UNIT'}
                                                                onClose={() => {
                                                                        setIsModalOpen(false);
                                                                        sessionStorage.removeItem(
                                                                                'supply',
                                                                        );
                                                                }}
                                                                title="IO Head"
                                                                initialValues={{
                                                                        packageTypes:
                                                                                selectedPackages,
                                                                        unitCommercial:
                                                                                unitCommercial,
                                                                }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );
                                                                        dispatch(
                                                                                addProduct({
                                                                                        IO_NO: ioNo,
                                                                                        REV_NO: revisedNo,
                                                                                        HEAD_TYPE: headtType,
                                                                                        COMMERCIAL_CODE:
                                                                                                values?.unitCommercial,
                                                                                        PRODUCT_CODE:
                                                                                                values?.packageTypes?.map(
                                                                                                        (
                                                                                                                pkg,
                                                                                                        ) =>
                                                                                                                pkg.typeCode,
                                                                                                ) ||
                                                                                                [],
                                                                                        PRODUCT_TYPE_CODE:
                                                                                                values?.productTypeCode,
                                                                                }),
                                                                        );
                                                                        setSelectedPackages([]);
                                                                        setUnitCommercial(null);
                                                                }}
                                                                labelHeading="Product"
                                                                placeholder="Search"
                                                                supplyOptions={productTypeOptions}
                                                                packageOptions={productOptions}
                                                                options={formattedData}
                                                        />
                                                )}
                                                {loginType === 'REGION' && (
                                                        <CheckboxModel
                                                                isOpen={loginType !== 'UNIT'}
                                                                onClose={() =>
                                                                        setIsModalOpen(false)
                                                                }
                                                                title="Select Packages"
                                                                initialValues={{ packageTypes: [] }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );

                                                                        if (headtType) {
                                                                                dispatch(
                                                                                        addPackage({
                                                                                                IO_NO: ioNo,
                                                                                                REV_NO: revisedNo,
                                                                                                HEAD_TYPE: headtType,
                                                                                                COMMERCIAL_CODE:
                                                                                                        values?.unitCommercial,
                                                                                                PACKAGE_ID:
                                                                                                        values?.packageTypes?.map(
                                                                                                                (
                                                                                                                        pkg,
                                                                                                                ) =>
                                                                                                                        pkg.PACKAGE_ID,
                                                                                                        ) ||
                                                                                                        [],
                                                                                        }),
                                                                                );
                                                                        }
                                                                }}
                                                                labelHeading={'Customer Package'}
                                                                placeholder={'Search'}
                                                                options={formattedData}
                                                                headType={headtType}
                                                        />
                                                )}
                                        </>
                                )}
                        </div>
                </>
        );
};

export default ProjectCostTable;
